<template>
	<div :class="classes">
		<div
			class="w-full relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
			<router-link :to="links[0].url" class="flex items-center" @click="handleClick">
				<div
					class="w-24 flex justify-center rounded-md p-3"
					:class="{
						'bg-mabey-950': theme == 'primary',
						'bg-mabey-600': theme == 'secondary'
					}">
					<font-awesome-icon
						v-if="icon"
						:icon="['fas', icon]"
						class="w-100 text-white text-6xl"
						:class="transforms" />
				</div>
				<div class="ml-5 truncate text-lg font-medium text-gray-500">
					<slot></slot>
					<template v-if="showCount">
						<div v-if="loading" class="w-full flex justify-center">
							<app-loader />
						</div>
						<p v-else class="text-3xl font-semibold text-mabey-950">{{ count }}</p>
					</template>
				</div>
			</router-link>
			<div v-if="showCount || links.length > 1" class="ml-16 flex items-baseline pb-6 sm:pb-7">
				<div class="absolute inset-x-0 bottom-0 text-start bg-gray-50 px-4 py-4 sm:px-6">
					<div class="flex justify-between text-sm">
						<router-link
							v-for="(link, index) in links"
							:key="index"
							:to="link.url"
							class="font-medium text-mabey-600">
							{{ link.label ?? 'View all' }}
							<span class="sr-only"
								><slot></slot> - {{ link.label ?? 'View all' }}</span
							>
						</router-link>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted } from 'vue'
	import ContractService from '@/services/contract'
	import StockService from '@/services/stock'
	import useAuthStore from '@/stores/auth'
	import useToasterStore from '@/stores/toaster'

	// Resources
	const api = new ContractService()
	const stockApi = new StockService()
	const auth = useAuthStore()
	const toaster = useToasterStore()

	// Data
	const loading = ref(true)
	const count = ref<string | Number | null>(null)

	type Link = {
		url: string
		label: string
	}

	const props = withDefaults(
		defineProps<{
			icon?: string
			transforms?: string
			label?: string
			theme?: 'primary' | 'secondary'
			links: Link[]
			area?: 'stock-check' | 'pick' | 'load-delivery'
			showCount?: boolean
		}>(),
		{
			links: undefined,
			icon: undefined,
			label: '',
			theme: 'primary',
			transforms: '',
			area: undefined,
			showCount: true
		}
	)

	const emit = defineEmits(['click'])

	const handleClick = () => {
		if (!props.links) {
			emit('click')
		}
	}

	const classes = computed(() => ({
		'flex items-center justify-between mt-3 rounded w-full': true
	}))

	const getData = () => {
		if (!props.showCount) {
			loading.value = false
			count.value = null
			return
		}

		if (props.area === 'stock-check') {
			stockApi
				.getStockCheckCounts(auth.user?.assigned_depots)
				.then((res) => {
					loading.value = false
					if (res.total_items !== undefined) {
						count.value = res.total_items
					} else if (res.data && res.data.total_items !== undefined) {
						count.value = res.data.total_items
					} else if (res.data && res.data.data && res.data.data.total_items !== undefined) {
						count.value = res.data.data.total_items
					} else {
						count.value = 0
					}
				})
				.catch((error) => {
					console.error(error)
					count.value = 'N/A'
					toaster.error({ text: `An error occurred retrieving ${props.label} data` })
					loading.value = false
				})
			return
		}

		if (props.area && auth.user?.assigned_depots) {
			const encodedDepots = auth.user.assigned_depots
				.map((value) => encodeURIComponent(value))
				.join('|')

			api.getYardAppArea(auth.user.id, props.area, encodedDepots)
				.then((res) => {
					loading.value = false

					if (res.data.meta && res.data.meta.paging && res.data.meta.paging.total) {
						count.value = res.data.meta.paging.total
					} else {
						count.value = 0
					}
				})
				.catch((error) => {
					console.error(error)
					count.value = 'N/A'
					toaster.error({ text: `An error occurred retrieving ${props.label} data` })
					loading.value = false
				})
		} else {
			loading.value = false
			count.value = 'N/A'

			if (!auth.user?.assigned_depots) {
				console.warn('Authenticated user has no assigned depots')
			}

			if (!props.area) {
				console.warn('Invalid area configuration for ' + props.label)
			}
		}
	}

	onMounted(() => {
		getData()
	})
</script>
